import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-bold uppercase tracking-[0.08em] transition-all disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  {
    variants: {
      variant: {
        default: 'bg-cosmos-green text-white hover:bg-cosmos-green-dark hover:-translate-y-0.5 shadow-sm',
        gold: 'bg-cosmos-gold text-cosmos-black hover:bg-cosmos-gold-dark hover:-translate-y-0.5 shadow-sm',
        dark: 'bg-pitch-900 text-white hover:bg-cosmos-green border-2 border-pitch-900 hover:border-cosmos-green',
        outline: 'border-2 border-current bg-transparent hover:bg-cosmos-green/10 text-foreground',
        ghost: 'bg-transparent text-foreground hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        link: 'text-cosmos-green underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-6 text-xs',
        sm: 'h-9 px-4 text-[11px]',
        lg: 'h-14 px-8 text-sm',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'

export { buttonVariants }
